import React, { useState, useEffect } from 'react';
import { useAuth } from '../services/AuthContext';
import { stripeAPI, userAPI } from '../services/api';
import CoachSidebar from '../components/Dashboard/CoachSidebar';
import EditAvatarModal from '../components/Modals/EditAvatarModal';
import EditPromptModal from '../components/Modals/EditPromptModal';
import ChangeSeatsModal from '../components/Modals/ChangeSeatsModal';
import PurchaseSeatsModal from '../components/Modals/PurchaseSeatsModal';

const Finances = () => {
  const { user, updateUser } = useAuth();
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [isSeatsModalOpen, setIsSeatsModalOpen] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isConnectingStripe, setIsConnectingStripe] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  // Poll for user data to get updated stripeConnectStatus
  useEffect(() => {
    if (user && user.id && user.id !== 'dev-user') {
      userAPI.getById(user.id).then((res) => {
        if (res.data && res.data.stripeConnectStatus !== user.stripeConnectStatus) {
          updateUser({ stripeConnectStatus: res.data.stripeConnectStatus });
        }
      }).catch(console.error);
    }
  }, [user?.id]);

  const handleStripeOnboarding = async () => {
    try {
      setIsConnectingStripe(true);
      const res = await stripeAPI.onboardCoach();
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Failed to start Stripe onboarding:', error);
      alert('Failed to connect to Stripe. Please try again.');
    } finally {
      setIsConnectingStripe(false);
    }
  };

  const handleSubscribePathA = async () => {
    try {
      const seats = parseInt(prompt('How many seats would you like to purchase? (Minimum 10)', '10'), 10);
      if (!seats || seats < 10) {
        alert('Minimum 10 seats required for Path A.');
        return;
      }
      const res = await stripeAPI.subscribePathA(seats);
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Failed to subscribe:', error);
      alert('Failed to start subscription process.');
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await stripeAPI.createPortalSession();
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      alert('Failed to open billing portal.');
    }
  };

  const [stats, setStats] = useState({
    activeClientsCount: 0,
    activePathACount: 0,
    activePathBCount: 0,
    projectedPathBRevenue: 0,
    pendingEarnings: 0,
    activeStreams: [],
    hasPathASubscription: false,
    pathASeats: 0,
    currentPeriodEnd: null
  });
  const [loadingStats, setLoadingStats] = useState(true);

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const res = await stripeAPI.getFinancesStats();
      if (res.data) {
        setStats(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch finances stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleWithdraw = async () => {
    if (!window.confirm(`Withdraw $${stats.pendingEarnings.toFixed(2)} to your connected Stripe account?`)) return;
    try {
      setIsWithdrawing(true);
      const res = await stripeAPI.requestPayout();
      if (res.data?.success) {
        alert(`Successfully withdrawn $${res.data.withdrawn.toFixed(2)}!`);
        await fetchStats();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Withdrawal failed. Please try again.');
    } finally {
      setIsWithdrawing(false);
    }
  };

  const hasPathASubscription = stats.hasPathASubscription;
  const isStripeConnected = user?.stripeConnectStatus === 'active';
  const pendingEarnings = stats.pendingEarnings || 0;

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      
      <EditAvatarModal isOpen={isAvatarModalOpen} onClose={() => setIsAvatarModalOpen(false)} currentAvatar="" />
      <EditPromptModal isOpen={isPromptModalOpen} onClose={() => setIsPromptModalOpen(false)} />
      <ChangeSeatsModal
        isOpen={isSeatsModalOpen}
        onClose={() => setIsSeatsModalOpen(false)}
        currentSeats={stats.pathASeats}
        onUpdateSuccess={fetchStats}
      />
      <PurchaseSeatsModal
        isOpen={isPurchaseModalOpen}
        onClose={() => setIsPurchaseModalOpen(false)}
      />

      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">
            
            <main className="flex-1 flex flex-col">
              <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-6 md:mb-8">Finances</h1>

              {/* Path A Subscription Box */}
              <div className="bg-[var(--surface-white)] border border-[var(--border-light)] shadow-xl shadow-black/5 rounded-3xl p-6 mb-6">
                {/* Header */}
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-10 h-10 rounded-2xl bg-[var(--tangerine)]/10 flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-[var(--tangerine)] text-xl">chair</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-[var(--text-primary)]">Path A — You pay for your clients</h3>
                      {hasPathASubscription && (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setIsSeatsModalOpen(true)}
                            className="text-sm font-bold text-tangerine hover:text-orange-600 transition-colors"
                          >
                            Change plan
                          </button>
                          <span className="text-[var(--text-secondary)]/20 text-sm font-light">|</span>
                          <button
                            onClick={handleManageBilling}
                            className="text-sm font-bold text-tangerine hover:text-orange-600 transition-colors"
                          >
                            Manage Billing
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] mt-1">
                      Buy a block of seats from Ally and invite clients for free. You pay $100/seat/month, minimum 10 seats.
                      Clients get access at no charge — the cost is entirely on you.
                    </p>
                  </div>
                </div>

                {/* Status / Action */}
                {hasPathASubscription ? (
                  <div className="flex items-start sm:items-center gap-4 p-4 bg-green-500/5 border border-green-500/20 rounded-2xl">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-white text-lg">check</span>
                    </div>
                    <div className="flex-1 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <p className="font-bold text-[var(--text-primary)]">Active Subscription</p>
                        <p className="text-sm text-[var(--text-secondary)]">
                          {stats.pathASeats} seats available · clients you approve on Path A get free access
                        </p>
                      </div>
                      {stats.currentPeriodEnd && (
                        <div className="text-xs text-[var(--text-secondary)] bg-[var(--surface-white)] border border-[var(--border-light)] px-3.5 py-2 rounded-xl font-medium shrink-0 flex items-center gap-1.5 shadow-sm">
                          <span className="material-symbols-outlined text-xs text-[var(--text-secondary)]">calendar_today</span>
                          <span>Next payment: <span className="font-bold text-[var(--text-primary)]">{new Date(stats.currentPeriodEnd).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span></span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-[var(--off-white)] border border-[var(--border-light)] rounded-2xl">
                    <div className="text-sm text-[var(--text-secondary)]">
                      <span className="font-bold text-[var(--text-primary)]">Not subscribed.</span> You can still approve clients via Path B.
                    </div>
                    <button
                      onClick={() => setIsPurchaseModalOpen(true)}
                      className="shrink-0 px-5 py-2.5 bg-tangerine text-white font-bold rounded-xl shadow shadow-tangerine/20 hover:-translate-y-0.5 transition-all text-sm"
                    >
                      Subscribe — from $1,000/mo
                    </button>
                  </div>
                )}
              </div>

              {/* Path B Box */}
              <div className="bg-[var(--surface-white)] border border-[var(--border-light)] shadow-xl shadow-black/5 rounded-3xl p-6 mb-8">
                {/* Header */}
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-10 h-10 rounded-2xl bg-blue-500/10 flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-blue-500 text-xl">account_balance</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[var(--text-primary)]">Path B — Clients pay directly</h3>
                    <p className="text-sm text-[var(--text-secondary)] mt-1">
                      You approve a client and set their monthly price (min $120). The client pays Ally,
                      and Ally automatically transfers your share to your bank account.
                      Ally keeps at least $100 or half the price — whichever is more.
                      Everything above that goes to you.
                    </p>
                  </div>
                </div>

                {/* Revenue split example */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    { price: 120, ally: 100, coach: 20 },
                    { price: 200, ally: 100, coach: 100 },
                    { price: 400, ally: 200, coach: 200 },
                  ].map(({ price, ally, coach }) => (
                    <div key={price} className="p-3 bg-[var(--off-white)] border border-[var(--border-light)] rounded-2xl text-center">
                      <p className="text-xs text-[var(--text-secondary)] mb-1">Client pays</p>
                      <p className="text-base font-bold text-[var(--text-primary)] mb-2">${price}/mo</p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-[var(--text-secondary)]">Ally gets</span>
                          <span className="font-bold text-[var(--text-primary)]">${ally}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[var(--text-secondary)]">You get</span>
                          <span className="font-bold text-tangerine">${coach}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Accumulated Balance / Payout Card */}
                <div className="p-5 mb-5 bg-[var(--off-white)] border border-[var(--border-light)] rounded-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-green-600 text-xl font-bold">payments</span>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-secondary)] font-medium">Accumulated Earnings</p>
                      <p className="text-2xl font-black text-[var(--text-primary)]">${pendingEarnings.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full md:w-auto">
                    {!isStripeConnected && pendingEarnings > 0 && (
                      <span className="text-xs text-orange-500 flex items-center gap-1 font-medium bg-orange-500/5 px-2.5 py-1 rounded-lg border border-orange-500/10">
                        <span className="material-symbols-outlined text-sm">warning</span>
                        Connect Stripe below to withdraw
                      </span>
                    )}
                    <button
                      onClick={handleWithdraw}
                      disabled={isWithdrawing || pendingEarnings <= 0 || !isStripeConnected}
                      className="w-full sm:w-auto px-5 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-neutral-800 disabled:text-gray-500 dark:disabled:text-neutral-500 text-white font-bold rounded-xl shadow shadow-green-600/10 hover:-translate-y-0.5 disabled:transform-none transition-all text-sm flex items-center justify-center gap-2"
                    >
                      <span className="material-symbols-outlined text-base">account_balance_wallet</span>
                      {isWithdrawing ? 'Withdrawing...' : 'Withdraw to Stripe'}
                    </button>
                  </div>
                </div>

                {/* Stripe Connect Status */}
                {isStripeConnected ? (
                  <div className="flex items-center gap-3 p-4 bg-green-500/5 border border-green-500/20 rounded-2xl">
                    <div className="w-9 h-9 bg-green-500 rounded-full flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-white text-base">check</span>
                    </div>
                    <div>
                      <p className="font-bold text-[var(--text-primary)] text-sm">Stripe account connected</p>
                      <p className="text-xs text-[var(--text-secondary)]">Payouts will be transferred automatically after each client payment.</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-2xl">
                    <div className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-orange-500 text-xl shrink-0 mt-0.5">warning</span>
                      <div className="flex-1">
                        <p className="font-bold text-[var(--text-primary)] text-sm">Stripe account not connected</p>
                        <p className="text-xs text-[var(--text-secondary)] mt-1 mb-3">
                          Without this, Ally cannot send your earnings to your bank. Your clients can still pay,
                          but payouts will be held until you complete the setup.
                        </p>
                        <button
                          onClick={handleStripeOnboarding}
                          disabled={isConnectingStripe}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-tangerine text-white text-sm font-bold rounded-xl shadow shadow-tangerine/20 hover:-translate-y-0.5 transition-all disabled:opacity-50"
                        >
                          <span className="material-symbols-outlined text-base">link</span>
                          {isConnectingStripe ? 'Connecting...' : 'Connect Stripe Account'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {loadingStats ? (
                <div className="bg-[var(--surface-white)] border border-[var(--border-light)] shadow rounded-3xl p-6 mb-8 flex justify-center items-center h-24">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tangerine"></div>
                </div>
              ) : (
                <div className="bg-green-500/10 border-green-500/20 rounded-2xl p-4 mb-8">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="material-symbols-outlined text-green-500 text-xl">analytics</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-700 dark:text-green-400">
                        Live Metrics Active
                      </h3>
                      <div className="mt-1 text-xs text-[var(--text-secondary)]">
                        <p>
                          Your financial overview is now connected to real-time database calculations based on active referred clients and your pricing configurations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Revenue Overview */}
              <div className="bg-[var(--surface-white)] overflow-hidden shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">💰</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-[var(--text-secondary)] truncate">
                          Projected Monthly Revenue
                        </dt>
                        <dd className="text-lg font-black text-[var(--text-primary)]">
                          ${stats.projectedPathBRevenue.toFixed(2)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending Payments */}
              <div className="bg-[var(--surface-white)] overflow-hidden shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">⏳</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-[var(--text-secondary)] truncate">
                          Accumulated Balance
                        </dt>
                        <dd className="text-lg font-black text-[var(--text-primary)]">
                          ${stats.pendingEarnings.toFixed(2)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Clients */}
              <div className="bg-[var(--surface-white)] overflow-hidden shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">👥</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-[var(--text-secondary)] truncate">
                          Active Clients
                        </dt>
                        <dd className="text-lg font-black text-[var(--text-primary)]">
                          {stats.activeClientsCount}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              {/* Path A Seats Filled */}
              <div className="bg-[var(--surface-white)] overflow-hidden shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">💺</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-[var(--text-secondary)] truncate">
                          Path A Seats Filled
                        </dt>
                        <dd className="text-lg font-black text-[var(--text-primary)]">
                          {stats.hasPathASubscription ? `${stats.activePathACount} / ${stats.pathASeats}` : 'Not Subscribed'}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Charts and Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Breakdown */}
              <div className="bg-[var(--surface-white)] overflow-hidden shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] mb-4">Revenue Breakdown</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[var(--text-secondary)]">Path B projected revenue share</span>
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        ${stats.projectedPathBRevenue.toFixed(2)} ({stats.activeClientsCount > 0 ? Math.round((stats.activePathBCount / stats.activeClientsCount) * 100) : 0}%)
                      </span>
                    </div>
                    <div className="w-full bg-[var(--off-white)] rounded-full h-2">
                      <div className="bg-tangerine h-2 rounded-full" style={{ width: `${stats.activeClientsCount > 0 ? (stats.activePathBCount / stats.activeClientsCount) * 100 : 0}%` }}></div>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[var(--text-secondary)]">Path A Active Seats usage</span>
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        {stats.activePathACount} seats ({stats.pathASeats > 0 ? Math.round((stats.activePathACount / stats.pathASeats) * 100) : 0}%)
                      </span>
                    </div>
                    <div className="w-full bg-[var(--off-white)] rounded-full h-2">
                      <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${stats.pathASeats > 0 ? (stats.activePathACount / stats.pathASeats) * 100 : 0}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Revenue Streams */}
              <div className="bg-[var(--surface-white)] overflow-hidden shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] mb-4">Active Revenue Streams</h3>
                  <div className="space-y-4 max-h-[300px] overflow-y-auto">
                    {stats.activeStreams.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-[var(--text-secondary)]">No active Path B revenue streams yet.</p>
                      </div>
                    ) : (
                      stats.activeStreams.map((stream: any) => (
                        <div key={stream.id} className="flex items-center justify-between p-3 bg-green-500/5 border border-green-500/20 rounded-2xl">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                              $
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[var(--text-primary)]">{stream.clientName}</p>
                              <p className="text-xs text-[var(--text-secondary)]">{stream.clientEmail}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-green-600">+${stream.coachShare.toFixed(2)}/mo</p>
                            <p className="text-[10px] text-[var(--text-secondary)]">Client price ${stream.price}/mo</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction History */}
            <div className="mt-8 bg-[var(--surface-white)] shadow-xl shadow-black/5 border border-[var(--border-light)] rounded-3xl">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-bold text-[var(--text-primary)] mb-6">
                  Transaction History
                </h3>
                {!stats.transactions || stats.transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-[var(--text-secondary)]/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="mt-2 text-sm text-[var(--text-secondary)]">
                      Transaction history will be displayed here after the first payments.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-[var(--border-light)]/40">
                      <thead>
                        <tr className="text-left text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">
                          <th className="pb-3">Date</th>
                          <th className="pb-3">Description</th>
                          <th className="pb-3">Type</th>
                          <th className="pb-3">Status</th>
                          <th className="pb-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--border-light)]/40 text-sm">
                        {stats.transactions.map((tx) => {
                          const isEarning = tx.type === 'earning';
                          const isPayout = tx.type === 'payout';
                          return (
                            <tr key={tx.id} className="hover:bg-[var(--off-white)]/30 transition-colors">
                              <td className="py-4 whitespace-nowrap text-[var(--text-secondary)] font-medium">
                                {new Date(tx.createdAt).toLocaleDateString('en-US', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </td>
                              <td className="py-4 font-bold text-[var(--text-primary)]">
                                {tx.description}
                              </td>
                              <td className="py-4">
                                <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${
                                  isEarning ? 'text-green-500 bg-green-500/10' :
                                  isPayout ? 'text-blue-500 bg-blue-500/10' :
                                  'text-red-500 bg-red-500/10'
                                }`}>
                                  {tx.type}
                                </span>
                              </td>
                              <td className="py-4">
                                <span className={`text-xs font-bold ${tx.status === 'succeeded' ? 'text-green-500' : 'text-yellow-500'}`}>
                                  ● {tx.status === 'succeeded' ? 'Succeeded' : 'Pending'}
                                </span>
                              </td>
                              <td className={`py-4 text-right font-black ${
                                isEarning ? 'text-green-500' : 'text-[var(--text-primary)]'
                              }`}>
                                {isEarning ? '+' : ''}${tx.amount.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            
            </main>

            <CoachSidebar 
              activeTab="Business"
              onOpenAvatarModal={() => setIsAvatarModalOpen(true)}
              onOpenPromptModal={() => setIsPromptModalOpen(true)}
              onOpenSettingsModal={() => { window.location.href = '/coach/settings' }}
            />

          </div>
        </div>
      </div>
    </div>
  );
};

export default Finances;
